import { useState } from 'react';
import {
  PARTNER_MATERIAL_LOCALES,
  partnerMaterialFormatOf,
  type PartnerMaterialAdmin
} from '@bliks-io/contracts';
import {
  useCreatePartnerMaterial,
  useRemovePartnerMaterialFile,
  useUpdatePartnerMaterial,
  useUploadPartnerMaterialFile
} from '@/pages/admin/api/mutations/usePartnerMaterials';
import {
  toCreateRequest,
  toUpdateRequest,
  type MaterialForm,
  type PendingFiles
} from './materialForm';

/**
 * Saves text, then files, then the publish state, in that order: a material
 * only goes live once every file it promises is in storage.
 */
export function useSaveMaterial() {
  const createMaterial = useCreatePartnerMaterial();
  const updateMaterial = useUpdatePartnerMaterial();
  const uploadFile = useUploadPartnerMaterialFile();
  const removeFile = useRemovePartnerMaterialFile();
  const [isSaving, setIsSaving] = useState(false);

  const save = async ({
    existing,
    values,
    pending,
    publish
  }: {
    existing: PartnerMaterialAdmin | null;
    values: MaterialForm;
    pending: PendingFiles;
    publish: boolean;
  }): Promise<PartnerMaterialAdmin> => {
    setIsSaving(true);
    try {
      let material = existing
        ? await updateMaterial.mutateAsync({ id: existing._id, data: toUpdateRequest(values) })
        : await createMaterial.mutateAsync(toCreateRequest(values));

      for (const locale of pending.removals) {
        material = await removeFile.mutateAsync({ id: material._id, locale });
      }
      for (const locale of PARTNER_MATERIAL_LOCALES) {
        const file = pending.uploads[locale];
        const format = file && partnerMaterialFormatOf(file.type);
        if (!file || !format) continue;
        material = await uploadFile.mutateAsync({ id: material._id, locale, format, file });
      }
      if (material.active !== publish) {
        material = await updateMaterial.mutateAsync({
          id: material._id,
          data: { active: publish }
        });
      }
      return material;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
}
